import { useMemo } from 'react';
import type { MhProduct } from '@/types/mh';
import { groupProductsByCategory } from '@/utils/product-category';

const reviews = [
  {
    name: '张**',
    avatar: 'Z',
    avatarBg: 'bg-blue-500',
    rating: 5,
    content: '查询速度非常快，不到5分钟就拿到了结果，客服也很专业，全程保密，非常满意！',
    time: '2天前',
  },
  {
    name: '李**',
    avatar: 'L',
    avatarBg: 'bg-green-500',
    rating: 5,
    content: '结果很详细，完全符合我的需求。平台操作简单，支付也很方便，强烈推荐！',
    time: '5天前',
  },
  {
    name: '王**',
    avatar: 'W',
    avatarBg: 'bg-orange-500',
    rating: 5,
    content: '第一次用有点担心，结果出来后发现非常准确，解决了我的疑虑，以后有需要还会来。',
    time: '1周前',
  },
  {
    name: '陈**',
    avatar: 'C',
    avatarBg: 'bg-purple-500',
    rating: 5,
    content: '服务很靠谱，信息准确，客服响应及时，整个过程很顺畅，值得信赖的平台。',
    time: '2周前',
  },
];

type TestimonialsProps = {
  products: MhProduct[];
};

export default function Testimonials({ products }: TestimonialsProps) {
  const serviceNames = useMemo(() => (
    groupProductsByCategory(products)
      .map((group) => group.products[0]?.productName)
      .filter((name): name is string => Boolean(name))
  ), [products]);

  return (
    <section className="py-14 bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-10">
          <p className="text-blue-600 text-sm font-semibold mb-1 uppercase tracking-wider">Reviews</p>
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">用户评价</h2>
          <p className="text-gray-500 text-sm">来自真实用户的反馈</p>
        </div>

        {/* Overall rating */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-10 bg-white rounded-2xl p-6 border border-gray-100">
          <div className="text-center">
            <div className="text-5xl font-bold text-gray-900">4.9</div>
            <div className="flex justify-center gap-0.5 my-1">
              {[1,2,3,4,5].map(i => (
                <span key={i} className="text-yellow-400 text-xl">★</span>
              ))}
            </div>
            <div className="text-gray-400 text-xs">综合评分</div>
          </div>
          <div className="w-px h-16 bg-gray-100 hidden sm:block" />
          <div className="flex flex-col gap-1.5 flex-1 max-w-xs">
            {[
              { label: '5星', pct: 92 },
              { label: '4星', pct: 6 },
              { label: '3星', pct: 2 },
            ].map((r) => (
              <div key={r.label} className="flex items-center gap-2 text-xs text-gray-500">
                <span className="w-6 text-right">{r.label}</span>
                <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-yellow-400 rounded-full"
                    style={{ width: `${r.pct}%` }}
                  />
                </div>
                <span className="w-8">{r.pct}%</span>
              </div>
            ))}
          </div>
          <div className="w-px h-16 bg-gray-100 hidden sm:block" />
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">50万+</div>
            <div className="text-gray-400 text-xs mt-1">累计用户评价</div>
          </div>
        </div>

        {/* Review cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {reviews.map((r, index) => (
            <div key={r.name} className="bg-white rounded-2xl p-5 border border-gray-100 flex flex-col gap-3">
              <div className="flex items-center gap-3">
                <div className={`w-9 h-9 ${r.avatarBg} rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0`}>
                  {r.avatar}
                </div>
                <div>
                  <p className="font-semibold text-gray-800 text-sm">{r.name}</p>
                  <p className="text-gray-400 text-xs">
                    {serviceNames[index % serviceNames.length] ?? '信息查询服务'}
                  </p>
                </div>
              </div>
              <div className="flex gap-0.5">
                {Array.from({ length: r.rating }).map((_, i) => (
                  <span key={i} className="text-yellow-400 text-sm">★</span>
                ))}
              </div>
              <p className="text-gray-600 text-xs leading-relaxed flex-1">{r.content}</p>
              <p className="text-gray-300 text-xs">{r.time}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
